<template>
  <!-- Hidden until the user starts typing, so an untouched form isn't fronted
       by a list of unmet rules. v-if on the root only hides the markup - setup
       keeps running, so the `validity` the parent gates submit on stays live. -->
  <div v-if="password" class="mb-3 text-sm lh-140">
    <div
      v-for="check in checks"
      :key="check.key"
      class="d-flex align-items-start mb-1"
      :class="check.met ? 'text-success' : 'text-muted'"
    >
      <b-icon-check-circle-fill
        v-if="check.met"
        class="mt-1 mr-2 flex-shrink-0"
      ></b-icon-check-circle-fill>
      <b-icon-x-circle-fill
        v-else
        class="mt-1 mr-2 flex-shrink-0"
      ></b-icon-x-circle-fill>
      <span>
        {{ check.label }}
        <!-- For the character-variety rule, show which types are present so the
             user can see what to add rather than just that it failed. -->
        <span v-if="check.key === 'variety'" class="d-block">
          <span
            v-for="type in characterTypes"
            :key="type.key"
            class="mr-2"
            :class="type.met ? 'text-success' : 'text-muted'"
          >
            {{ type.met ? '&check;' : '&times;' }} {{ type.label }}
          </span>
        </span>
      </span>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, watch } from '@vue/composition-api'

// Entra's local-account password policy, which is what actually accepts or
// rejects the password - Django's AUTH_PASSWORD_VALIDATORS never run on this
// path, because native auth submits straight to Entra. MAX_LENGTH is our own
// tighter cap (Entra itself accepts up to 256) - also enforced as a hard
// `maxlength` on the actual input, not just flagged here after the fact.
const MIN_LENGTH = 8
const MAX_LENGTH = 64
// Entra requires characters from at least this many of the four types below,
// not all four. Showing all four as mandatory would mark a password Entra
// happily accepts (e.g. "CoralHarbour42", no symbol) as invalid.
const REQUIRED_CHARACTER_TYPES = 3
// Entra's native-auth password reset accepts a password containing a
// non-ASCII character (an accented letter, or a currency sign like £/€) at
// submit time, then silently fails to actually complete the reset later -
// found the hard way in production, with no useful error back from Entra.
// Checked client-side so this never reaches that failure mode at all.
const ASCII_ONLY = /^[\x20-\x7E]*$/

export default defineComponent({
  name: 'PasswordCriteriaChecklist',
  props: {
    password: {
      type: String,
      default: ''
    },
    // When provided, a "passwords match" row is shown. Left empty for screens
    // with no confirmation field.
    confirmation: {
      type: String,
      default: null
    }
  },
  setup(props, { emit }) {
    const characterTypes = computed(() => [
      {
        key: 'lower',
        label: 'lowercase',
        met: /[a-z]/.test(props.password)
      },
      {
        key: 'upper',
        label: 'uppercase',
        met: /[A-Z]/.test(props.password)
      },
      {
        key: 'digit',
        label: 'number',
        met: /[0-9]/.test(props.password)
      },
      {
        key: 'symbol',
        label: 'symbol',
        // Standard keyboard punctuation only - a currency sign like £/€ or an
        // accented letter is "not alphanumeric" too, but counting it here
        // would show a false checkmark for a character Entra rejects. Written
        // as the four contiguous ASCII punctuation ranges (0x21-0x2F,
        // 0x3A-0x40, 0x5B-0x60, 0x7B-0x7E) rather than hand-enumerated
        // characters, so nothing in the set is accidentally left out.
        met: /[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]/.test(props.password)
      }
    ])

    const typesMet = computed(
      () => characterTypes.value.filter((type) => type.met).length
    )

    const checks = computed(() => {
      // Back to two separate rows (one check each), but the text is still
      // drawn verbatim from the approved copy's single combined sentence -
      // split exactly at its "and", not reworded. The approved sentence
      // doesn't mention MAX_LENGTH at all, so it's enforced silently (met
      // condition + the input's hard `maxlength`) without inventing wording
      // that wasn't given.
      const rows = [
        {
          key: 'length',
          label: `Use at least ${MIN_LENGTH} characters`,
          met:
            props.password.length >= MIN_LENGTH &&
            props.password.length <= MAX_LENGTH
        },
        {
          key: 'variety',
          label: `Include characters from at least ${REQUIRED_CHARACTER_TYPES} of the following categories: uppercase letters, lowercase letters, numbers, and symbols.`,
          met: typesMet.value >= REQUIRED_CHARACTER_TYPES
        },
        {
          key: 'ascii',
          label: 'Only standard keyboard characters are supported, including English letters (A–Z), numbers, and symbols such as ! @ # $ % ^ & *. Accented characters and symbols such as £ or € are not supported.',
          met: ASCII_ONLY.test(props.password)
        }
      ]

      if (props.confirmation !== null) {
        rows.push({
          key: 'match',
          label: 'Both passwords match',
          met: !!props.password && props.password === props.confirmation
        })
      }

      return rows
    })

    // Lets the parent disable submit until every checkable rule passes, so a
    // password Entra would reject never costs a round trip. Emitted immediately
    // so the parent's gate starts in the correct position.
    const valid = computed(() => checks.value.every((check) => check.met))

    watch(valid, (isValid) => emit('validity', isValid), { immediate: true })

    return { checks, characterTypes, valid }
  }
})
</script>
